import React, { memo, useCallback, useMemo, useState, useEffect } from 'react'
import { Alert, Modal, Spin } from 'antd'
import { DefaultJsonForm } from '../../../../../../components/v2/default-json-form'
import schema from './schema.json'
import { convertData2JsonForm } from '../../../../../../shared/lib/convertData2JsonForm'
import { isFormDataRequired } from '../../../../../../../lib/utils/collections'
import fetchAPI from '../../../../../../../lib/utils/fetch-api'
import dayjs from 'dayjs'

interface TransferModalProps {
  isModalOpen: boolean
  employeeId: number
  currentDepartmentId?: number
  currentOfficeId?: number
  currentRegionId?: number
  onOk?: (() => void) | undefined
  onCancel?: ((e: React.MouseEvent<HTMLElement, MouseEvent>) => void) | undefined
  enums?: any
}

interface IFormData {
  department_id?: number
  office_id?: number
  region_id?: number
  transfer_date?: string
  comment?: string
}

interface ISchemaActual {
  resultObject: Record<string, never>
  resultSchema: {
    properties: {
      department_id: any
      office_id: any
      region_id: any
      transfer_date: any
      comment: any
    }
    required: string[]
  }
}

export const TransferModal: React.FC<TransferModalProps> = memo((props: TransferModalProps) => {
  const {
    isModalOpen,
    employeeId,
    currentDepartmentId,
    currentOfficeId,
    currentRegionId,
    onOk,
    onCancel,
    enums,
  } = props

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [formData, setFormData] = useState<IFormData>({
    department_id: undefined,
    office_id: undefined,
    region_id: undefined,
    transfer_date: undefined,
    comment: undefined,
  })

  const actualSchema = useMemo(() => {
    const schemaActual = convertData2JsonForm(schema.fields) as never as ISchemaActual

    if (schemaActual.resultSchema && schemaActual.resultSchema.properties) {
      // Добавляем enum для отдела (все отделы, включая текущий)
      // Enum-ы приходят с бэкенда с ключом 'department' (без префикса 'hr_')
      if (enums?.department) {
        const oneOf = enums.department.map((item: any) => ({
          const: item.id,
          title: item.value,
        }))
        const enumValues = enums.department.map((item: any) => item.id)

        schemaActual.resultSchema.properties.department_id = {
          ...schemaActual.resultSchema.properties.department_id,
          type: 'string',
          oneOf,
          enum: enumValues,
          view: {
            name: 'select-from-enum',
          },
          options: {
            ...schemaActual.resultSchema.properties.department_id.options,
            allowClear: true,
            disabled: loading,
          },
        }
      }

      // Добавляем enum для офиса (все офисы, включая текущий)
      if (enums?.office) {
        const oneOf = enums.office.map((item: any) => ({
          const: item.id,
          title: item.value,
        }))
        const enumValues = enums.office.map((item: any) => item.id)

        schemaActual.resultSchema.properties.office_id = {
          ...schemaActual.resultSchema.properties.office_id,
          type: 'string',
          oneOf,
          enum: enumValues,
          view: {
            name: 'select-from-enum',
          },
          options: {
            ...schemaActual.resultSchema.properties.office_id.options,
            allowClear: true,
            disabled: loading,
          },
        }
      }

      // Добавляем enum для района (все районы, включая текущий)
      // Enum-ы приходят с бэкенда с ключом 'region_id' (без префикса 'hr_')
      if (enums?.region_id) {
        const oneOf = enums.region_id.map((item: any) => ({
          const: item.id,
          title: item.value,
        }))
        const enumValues = enums.region_id.map((item: any) => item.id)

        schemaActual.resultSchema.properties.region_id = {
          ...schemaActual.resultSchema.properties.region_id,
          type: 'string',
          oneOf,
          enum: enumValues,
          view: {
            name: 'select-from-enum',
          },
          options: {
            ...schemaActual.resultSchema.properties.region_id.options,
            allowClear: true,
            disabled: loading,
          },
        }
      }

      // Делаем все поля disabled при загрузке
      Object.keys(schemaActual.resultSchema.properties).forEach(key => {
        if (!schemaActual.resultSchema.properties[key].options) {
          schemaActual.resultSchema.properties[key].options = {}
        }
        schemaActual.resultSchema.properties[key].options.disabled = loading
      })
    }

    return schemaActual.resultSchema || {}
  }, [enums, loading])

  const handleOk = useCallback(async () => {
    const isRequiredFields = isFormDataRequired(formData, actualSchema)
    if (!isRequiredFields) {
      Modal.warning({
        title: 'Предупреждение',
        content: 'Заполните все обязательные поля',
      })
      return
    }

    try {
      setLoading(true)
      setError(null)
      await fetchAPI(`/api/hr/employees/${employeeId}/transfer`, {
        method: 'POST',
        body: JSON.stringify(formData),
      })
      onOk?.()
    } catch (err: any) {
      setError(err?.message || 'Не удалось перевести сотрудника')
    } finally {
      setLoading(false)
    }
  }, [formData, actualSchema, employeeId, onOk])

  const handleCancel = (e: React.MouseEvent<HTMLElement>) => {
    setFormData({
      department_id: undefined,
      office_id: undefined,
      region_id: undefined,
      transfer_date: undefined,
      comment: undefined,
    })
    setError(null)
    onCancel?.(e)
  }

  const onChange = useCallback(data => {
    setFormData(prevData => {
      return {
        ...prevData,
        ...data.formData,
      }
    })
  }, [])

  // Проверяем, изменились ли значения перевода
  const isTransferUnchanged = useMemo(() => {
    const departmentChanged =
      formData.department_id !== undefined &&
      Number(formData.department_id) !== Number(currentDepartmentId)
    const officeChanged =
      formData.office_id !== undefined && Number(formData.office_id) !== Number(currentOfficeId)
    const regionChanged =
      formData.region_id !== undefined && Number(formData.region_id) !== Number(currentRegionId)

    // Если хотя бы одно поле изменилось, перевод не является неизменным
    return !departmentChanged && !officeChanged && !regionChanged
  }, [
    formData.department_id,
    formData.office_id,
    formData.region_id,
    currentDepartmentId,
    currentOfficeId,
    currentRegionId,
  ])

  // Устанавливаем значения по умолчанию при открытии модалки
  useEffect(() => {
    if (isModalOpen) {
      setFormData({
        department_id: currentDepartmentId,
        office_id: currentOfficeId,
        region_id: currentRegionId,
        transfer_date: dayjs().toISOString(),
        comment: undefined,
      })
      setError(null)
    }
  }, [isModalOpen, currentDepartmentId, currentOfficeId, currentRegionId])

  if (!isModalOpen) {
    return null
  }

  return (
    <Modal
      title={schema.title}
      onOk={handleOk}
      onCancel={handleCancel}
      open={isModalOpen}
      destroyOnHidden={true}
      okButtonProps={{
        disabled: loading || isTransferUnchanged,
      }}
      cancelButtonProps={{
        disabled: loading,
      }}
      closable={!loading}
      keyboard={!loading}
      maskClosable={!loading}
      okText="Подтвердить"
    >
      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}
      <Spin spinning={loading}>
        <DefaultJsonForm
          formData={formData}
          schema={actualSchema}
          onChange={onChange}
          orientation="horizontal"
        />
      </Spin>
    </Modal>
  )
})

TransferModal.displayName = 'TransferModal'
